import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Textarea,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  useToast,
  Spinner,
  Avatar,
  Divider,
} from "@chakra-ui/react";
import { MdSend, MdRefresh } from "react-icons/md";
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";

const TicketDetail = ({ ticket, onTicketUpdated }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const pageID = 20;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);

  // Fetch ticket messages
  const fetchTicketMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/tickets/ticket/messages?ticket_id=${ticket.id}&page_id=${pageID}`,
        "GET"
      );

      if (response.status === "success") {
        setMessages(response.data || []);
      } else {
        throw new Error(response.message || "Failed to fetch ticket messages");
      }
    } catch (error) {
      console.error("Error fetching ticket messages:", error);  
      toast({
        title: t("Error"),
        description: error.message || t("Failed to load ticket messages"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [ticket.id, toast, t]);

  // Initial fetch
  useEffect(() => {
    fetchTicketMessages();
  }, [fetchTicketMessages]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast({
        title: t("Validation Error"),
        description: t("Message cannot be empty"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setSending(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/tickets/ticket/messages?page_id=${pageID}`,
        "POST",
        {
          ticket_id: ticket.id,
          message: newMessage.trim(),
        }
      );

      if (response.status === "success") {
        setNewMessage("");
        fetchTicketMessages(); // Refresh messages
        toast({
          title: t("Success"),
          description: response.message || t("Message sent successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        throw new Error(response.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: t("Error"),
        description: error.message || t("Failed to send message"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSending(false);
    }
  };

  // Close ticket
  const handleCloseTicket = async () => {
    if (ticket.status === "closed") {
      toast({
        title: t("Info"),
        description: t("Ticket is already closed"),
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setClosingTicket(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/tickets/ticket/update/status?page_id=${pageID}`,
        "PATCH",
        {
          ticket_id: ticket.id,
          status: "closed",
        }
      );

      if (response.status === "success") {
        toast({
          title: t("Success"),
          description: response.message || t("Ticket closed successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        
        // Update the ticket status locally and notify parent component
        if (onTicketUpdated) {
          onTicketUpdated({ ...ticket, status: "closed" });
        }
      } else {
        throw new Error(response.message || "Failed to close ticket");
      }
    } catch (error) {
      console.error("Error closing ticket:", error);
      toast({
        title: t("Error"),
        description: error.message || t("Failed to close ticket"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setClosingTicket(false);
    }
  };

  // Get message sender display info
  const getSenderInfo = (message) => {
    if (message.sender_type === "client") {
      return {
        name: t("Client"),
        avatar: "C",
        color: "blue.500",
        bg: "blue.50",
      };
    } else {
      return {
        name: t("Support"),
        avatar: "S",
        color: "green.500",
        bg: "green.50",
      };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading && !ticket) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Ticket Header */}
      <Card mb={4}>
        <CardBody>
          <Flex justify="space-between" align="start" mb={4}>
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={2}>
                {ticket?.subject || ticket.subject}
              </Text>
              <Badge colorScheme={ticket.status === "open" ? "green" : "gray"}>
                {ticket?.status || ticket.status}
              </Badge>
            </Box>
            
            <HStack spacing={2}>
              <Button
                size="sm"
                leftIcon={<MdRefresh />}
                onClick={fetchTicketMessages}
                isLoading={loading}
              >
                {t("Refresh")}
              </Button>
              
              {ticket.status !== "closed" && (
                <Button
                  size="sm"
                  colorScheme="red"
                  onClick={handleCloseTicket}
                  isLoading={closingTicket}
                  loadingText={t("Closing...")}
                >
                  {t("Close Ticket")}
                </Button>
              )}
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Messages */}
      <Card mb={4}>
        <CardBody>
          <Text fontSize="md" fontWeight="semibold" mb={4}>
            {t("Messages")}
          </Text>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Spinner />
            </Box>
          ) : messages.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={4}>
              {t("No messages yet")}
            </Text>
          ) : (
            <VStack spacing={4} align="stretch" maxHeight="400px" overflowY="auto">
              {messages.map((message, index) => {
                const senderInfo = getSenderInfo(message);
                return (
                  <Box key={message.id || index}>
                    <Flex align="start" gap={3}>
                      <Avatar
                        size="sm"
                        name={senderInfo.name}
                        bg={senderInfo.color}
                        color="white"
                      />
                      <Box flex="1">
                        <Flex align="center" gap={2} mb={1}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {senderInfo.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatDate(message.created_at)}
                          </Text>
                        </Flex>
                        <Box
                          bg={senderInfo.bg}
                          p={3}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <Text fontSize="sm" whiteSpace="pre-wrap">
                            {message.message}
                          </Text>
                        </Box>
                      </Box>
                    </Flex>
                    {index < messages.length - 1 && <Divider mt={4} />}
                  </Box>
                );
              })}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Send Message */}
      <Card>
        <CardBody>
          <Text fontSize="md" fontWeight="semibold" mb={4}>
            {t("Send Message")}
          </Text>
          
          <VStack spacing={4} align="stretch">
            <Textarea
              isDisabled={ticket.status === "closed"}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t("Type your message here...")}
              rows={4}
              resize="vertical"
            />
            
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" color="gray.500">
                {newMessage.length}/1000 {t("characters")}
              </Text>
              
              <Button
                leftIcon={<MdSend />}
                colorScheme="cyan"
                isDisabled={ticket.status === "closed" || newMessage.length > 1000 || !newMessage.trim() }
                color="white"
                onClick={handleSendMessage}
                isLoading={sending}
                loadingText={t("Sending...")}
              >
                {t("Send Message")}
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default TicketDetail;
